import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, TextField, Typography,
} from '@mui/material';
import { ArrowBack, DeleteOutline } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getMyTicketDetail, getTicketDetail, updateTicket, deleteTicket, deleteMyTicket,
  getUploadUrl, uploadToBlob, addMyAttachments, deleteMyAttachment,
} from '../../services/feedbackService';
import { feedbackStatuses, adminPriorities } from '../../constants/statusOptions';
import { ROUTES } from '../../constants/routes';
import { STATUS_COLORS } from './feedbackShared';
import { DiagnosticsPanel } from './DiagnosticsPanel';
import { AttachmentLightbox } from './AttachmentLightbox';
import { ProposedSolutionCard } from './ProposedSolutionCard';
import { SubmitterEditPanel } from './SubmitterEditPanel';
import { SubmitterActions } from './SubmitterActions';
import { AttachmentsSection } from './AttachmentsSection';
import { CommentsThread } from './CommentsThread';
import { CopyableKey } from './CopyableKey';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import type { FeedbackTicketDetail } from '../../types/feedback';

interface Props { isAdmin?: boolean }

export const FeedbackDetailPage = ({ isAdmin }: Props) => {
  const { ticketKey } = useParams<{ ticketKey: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [ticket, setTicket] = useState<FeedbackTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editStatus, setEditStatus] = useState('');
  const [editAdminPri, setEditAdminPri] = useState('');
  const [editResolution, setEditResolution] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [lightbox, setLightbox] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  const backPath = isAdmin ? '/admin/feedback' : ROUTES.MY_FEEDBACK;

  const fetchTicket = useCallback(async () => {
    if (!ticketKey) return;
    setLoading(true);
    try {
      const t = isAdmin ? await getTicketDetail(ticketKey) : await getMyTicketDetail(ticketKey);
      setTicket(t);
      setEditStatus(t.Status);
      setEditAdminPri(t.AdminPriority || '');
      setEditResolution(t.ResolutionNote || '');
    } catch { setTicket(null); }
    finally { setLoading(false); }
  }, [ticketKey, isAdmin]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  const handleDelete = useCallback(async () => {
    if (!ticket) return;
    try {
      if (isAdmin) await deleteTicket(ticket.FeedbackTicketKey);
      else await deleteMyTicket(ticket.FeedbackTicketKey);
      navigate(backPath);
    } catch { /* */ }
  }, [ticket, isAdmin, navigate, backPath]);

  const handleSave = useCallback(async () => {
    if (!ticket) return;
    setSaving(true);
    try {
      const data: Record<string, unknown> = {};
      if (editStatus !== ticket.Status) data.Status = editStatus;
      if (editAdminPri !== (ticket.AdminPriority || '')) data.AdminPriority = editAdminPri || null;
      if (editResolution !== (ticket.ResolutionNote || '')) data.ResolutionNote = editResolution || null;
      if (Object.keys(data).length) await updateTicket(ticket.FeedbackTicketKey, data);
      navigate(backPath);
    } catch { /* */ }
    finally { setSaving(false); }
  }, [ticket, editStatus, editAdminPri, editResolution, navigate, backPath]);

  // Submitter can edit title/description/severity/priority and attachments
  // while the ticket is pre-solution ('New') or back in 'Needs Rework' after
  // rejecting a proposed solution. Once a solution is on the table
  // ('Ready for Testing' / 'Fixed') or the ticket is locked
  // ('Confirmed Fixed'), the submitter view is read-only.
  const EDITABLE_STATUSES = ['New', 'Needs Rework'];
  const LOCKED_STATUSES = ['Confirmed Fixed'];
  const canEdit = !isAdmin && !!ticket && EDITABLE_STATUSES.includes(ticket.Status);
  const isLocked = !!ticket && LOCKED_STATUSES.includes(ticket.Status);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (!ticket || !files.length) return;
    setUploadingAttachments(true);
    try {
      const payload: { FileName: string; BlobUrl: string; FileSize: number; ContentType: string }[] = [];
      for (const file of files) {
        const { uploadUrl, blobPath } = await getUploadUrl(file.name, file.type);
        await uploadToBlob(uploadUrl, file);
        payload.push({ FileName: file.name, BlobUrl: blobPath, FileSize: file.size, ContentType: file.type });
      }
      if (payload.length) {
        await addMyAttachments(ticket.FeedbackTicketKey, payload);
        await fetchTicket();
      }
    } catch { /* */ }
    finally { setUploadingAttachments(false); }
  }, [ticket, fetchTicket]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (!canEdit) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(new File([file], `clipboard-${Date.now()}-${i}.png`, { type: file.type }));
      }
    }
    if (imageFiles.length) {
      e.preventDefault();
      uploadFiles(imageFiles);
    }
  }, [canEdit, uploadFiles]);

  const handleRemoveAttachment = useCallback(async (attachmentKey: string) => {
    try {
      await deleteMyAttachment(attachmentKey);
      await fetchTicket();
    } catch { /* */ }
  }, [fetchTicket]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;
  if (!ticket) return (
    <Box sx={{ pt: 4, textAlign: 'center' }}>
      <Typography color="error">Ticket not found.</Typography>
      <Button onClick={() => navigate(backPath)} sx={{ mt: 2, textTransform: 'none' }}>Back</Button>
    </Box>
  );

  const statusColor = STATUS_COLORS[ticket.Status] || '#616161';

  return (
    <Box sx={{ mx: -3, mt: -2, mb: -2, minHeight: 'calc(100vh - 64px)', bgcolor: 'background.default' }}>
      {/* Header bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper', flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate(backPath)}><ArrowBack fontSize="small" /></IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{ticket.TicketNumber}</Typography>
        <CopyableKey value={ticket.FeedbackTicketKey} />
        <Chip label={ticket.Type === 'bug' ? 'Bug' : 'Feature'} size="small"
          sx={{ bgcolor: ticket.Type === 'bug' ? '#ffebee' : '#e8f5e9', color: ticket.Type === 'bug' ? '#d32f2f' : '#2e7d32', fontSize: '0.72rem' }} />
        <Chip label={ticket.Status} size="small" sx={{ bgcolor: alpha(statusColor, 0.12), color: statusColor, fontWeight: 600, fontSize: '0.72rem' }} />
        {ticket.Severity && <Chip label={`Severity: ${ticket.Severity}`} size="small" variant="outlined" sx={{ fontSize: '0.72rem' }} />}
        {ticket.Priority && <Chip label={`Priority: ${ticket.Priority}`} size="small" variant="outlined" sx={{ fontSize: '0.72rem' }} />}
        {ticket.AdminPriority && <Chip label={ticket.AdminPriority} size="small" color="warning" sx={{ fontSize: '0.72rem' }} />}
        {canEdit && (
          <Chip label="Editable" size="small"
            sx={{ fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.12), color: 'info.main', fontWeight: 600 }} />
        )}
        {isLocked && (
          <Chip label="Locked" size="small"
            sx={{ fontSize: '0.7rem', bgcolor: alpha(theme.palette.success.main, 0.14), color: 'success.main', fontWeight: 600 }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {isAdmin ? `by ${ticket.SubmittedByName || ticket.SubmittedByEmail} — ` : ''}
          {new Date(ticket.sysCreateDate).toLocaleString()}
        </Typography>
        {/* Submitters can't delete a ticket once they've locked it by confirming the fix. Admins always can. */}
        {(isAdmin || !isLocked) && (
          <Button variant="outlined" size="small" color="error" startIcon={<DeleteOutline />}
            onClick={() => setConfirmDlg({
              open: true,
              title: 'Delete Ticket',
              message: `Permanently delete ${ticket.TicketNumber}? This cannot be undone.`,
              confirmLabel: 'Delete',
              confirmColor: 'error',
              onConfirm: handleDelete,
            })}
            sx={{ textTransform: 'none', fontSize: '0.72rem', ml: 1 }}>
            Delete
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 3, p: 3, overflow: 'auto', height: 'calc(100vh - 64px - 52px)' }} onPaste={handlePaste}>
        {/* Main column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {canEdit ? (
            <SubmitterEditPanel ticket={ticket} onSaved={fetchTicket} />
          ) : (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: '1.1rem', lineHeight: 1.35 }}>
                {ticket.RefinedTitle || ticket.Title}
              </Typography>
              {ticket.RefinedTitle && ticket.RefinedTitle !== ticket.Title && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.72rem', fontStyle: 'italic' }}>
                  Submitted as: "{ticket.Title}"
                </Typography>
              )}
              {ticket.Description && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2.5, mt: ticket.RefinedTitle ? 0 : 1, borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{ticket.Description}</Typography>
                </Paper>
              )}
            </>
          )}

          {/* Comments sit right under the title/edit area — back-and-forth
              between submitter and admin is the most actively-read content,
              so it stays above attachments, the proposed solution, and the
              long diagnostics panel. */}
          <CommentsThread
            ticketKey={ticket.FeedbackTicketKey}
            comments={ticket.Comments || []}
            isAdmin={!!isAdmin}
            locked={isLocked || ticket.Status === 'Deferred'}
            onPosted={fetchTicket}
          />

          <AttachmentsSection
            attachments={ticket.Attachments || []}
            canEdit={canEdit}
            uploading={uploadingAttachments}
            onOpenLightbox={(index) => setLightbox({ open: true, index })}
            onFilesPicked={uploadFiles}
            onRequestRemove={(att) => setConfirmDlg({
              open: true,
              title: 'Remove Attachment',
              message: `Remove ${att.FileName} from this ticket?`,
              confirmLabel: 'Remove', confirmColor: 'error',
              onConfirm: () => handleRemoveAttachment(att.FeedbackAttachmentKey),
            })}
          />

          {ticket.SolutionSummary && (
            <ProposedSolutionCard
              solutionSummary={ticket.SolutionSummary}
              testProof={ticket.TestProof}
              proposedBy={ticket.SolutionProposedBy}
              proposedAt={ticket.SolutionProposedAt}
            />
          )}

          {ticket.ResolutionNote && !isAdmin && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: alpha(theme.palette.success.main, 0.04), borderColor: alpha(theme.palette.success.main, 0.2) }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main', mb: 0.5 }}>Resolution Note</Typography>
              <Typography variant="body2">{ticket.ResolutionNote}</Typography>
            </Paper>
          )}

          {/* Full diagnostics — visible to everyone viewing the ticket */}
          <DiagnosticsPanel ticket={ticket} isAdmin={isAdmin} />
        </Box>

        {/* Right sidebar — admin actions / submitter actions + activity */}
        <Box sx={{ width: 340, flexShrink: 0 }}>
          {!isAdmin && <SubmitterActions ticket={ticket} onChanged={fetchTicket} />}

          {/* Admin Actions — prominent card at top */}
          {isAdmin && (
            <Paper variant="outlined" sx={{
              p: 2, borderRadius: 1.5, mb: 2,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.85rem', color: 'primary.main' }}>
                Admin Actions
              </Typography>
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Status</InputLabel>
                <Select value={editStatus} label="Status" onChange={(e) => setEditStatus(e.target.value)}>
                  {feedbackStatuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Admin Priority</InputLabel>
                <Select value={editAdminPri} label="Admin Priority" onChange={(e) => setEditAdminPri(e.target.value)}>
                  <MenuItem value="">None</MenuItem>
                  {adminPriorities.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Resolution Note" fullWidth multiline rows={3} size="small" value={editResolution}
                onChange={(e) => setEditResolution(e.target.value)}
                placeholder="What was done to resolve this..." sx={{ mb: 1.5 }} />
              <Button variant="contained" fullWidth size="small" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Paper>
          )}

          {ticket.Events && ticket.Events.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.85rem' }}>Activity</Typography>
              {ticket.Events.map((ev) => (
                <Box key={ev.FeedbackEventKey} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'baseline' }}>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', flexShrink: 0 }}>
                    {new Date(ev.sysCreateDate).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    {ev.EventType}{ev.OldValue && ev.NewValue ? `: ${ev.OldValue} → ${ev.NewValue}` : ''}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Box>
      </Box>

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
      <AttachmentLightbox
        open={lightbox.open}
        attachments={(ticket.Attachments || []).filter((a) => a.ContentType?.startsWith('image/'))}
        index={lightbox.index}
        onIndexChange={(i) => setLightbox({ open: true, index: i })}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
    </Box>
  );
};

export default FeedbackDetailPage;
